import React from 'react';
import { useLocation } from '@tanstack/react-router';
import { Box, Alert, CircularProgress } from '@mui/material';
import { GridWithCommands, GridDialogs } from '../../grid/components';
import { whenCommand } from '../../grid/commands/utils';
import { useGridPageLogic } from '../../grid/hooks';
import { useDeleteEntityMutation } from '../../grid/api/gridApi';
import type { NavigationRequestOptions } from '../../grid/types/navigation';

type Props = {
  nodeKey: string;
};

export const DashboardGridSection = ({ nodeKey }: Props) => {
  const { pathname } = useLocation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isResetFiltersDialogOpen, setIsResetFiltersDialogOpen] = React.useState(false);
  const [deletingRow, setDeletingRow] = React.useState<Record<string, unknown> | null>(null);
  const [deleteEntity, { isLoading: isDeleting }] = useDeleteEntityMutation();

  const gridLogic = useGridPageLogic({
    nodeKey,
    enableBatchOperations: true,
    enableNavigationGuard: false,
  });

  const {
    metadata: gridMetadata,
    parsedColumns,
    metadataLoading,
    metadataError,
    dataError,
    dataLoading,
  } = gridLogic;

  const navGuard = gridLogic.navGuard!;
  const hasDetailForm = !gridLogic.inlineEditable;

  const handleEdit = React.useCallback(
    (row: Record<string, unknown>) => {
      if (hasDetailForm && gridMetadata?.contentKey && gridMetadata?.contentId) {
        navGuard.requestNavigation(`${pathname}/${String(row.Id)}`, {
          search: {
            gridContentKey: gridMetadata.contentKey,
          },
        });
      }
    },
    [hasDetailForm, gridMetadata, pathname, navGuard]
  );

  const handleDelete = React.useCallback(
    (row: Record<string, unknown>) => {
      if (!gridLogic.inlineEditable) {
        setDeletingRow(row);
        setIsDeleteDialogOpen(true);
      } else {
        gridLogic.handleInlineRowDelete(row);
      }
    },
    [gridLogic]
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deletingRow?.Id || !gridMetadata?.contentKey) return;
    try {
      await deleteEntity({ id: String(deletingRow.Id), nodeKey: gridMetadata.contentKey }).unwrap();
      setIsDeleteDialogOpen(false);
      setDeletingRow(null);
      gridLogic.refetchGridData();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  }, [deletingRow, gridMetadata, deleteEntity, gridLogic]);

  const handleConfirmResetFilters = React.useCallback(() => {
    gridLogic.gridFiltering.resetAllFilters();
    setIsResetFiltersDialogOpen(false);
  }, [gridLogic]);

  const handleNavigate = React.useCallback(
    (path: string | number, options?: NavigationRequestOptions) => {
      navGuard.requestNavigation(typeof path === 'number' ? (path as -1) : path, options);
    },
    [navGuard]
  );

  if (metadataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (metadataError) {
    return <Alert severity="error">Failed to load grid: {JSON.stringify(metadataError)}</Alert>;
  }

  if (!gridMetadata) {
    return <Alert severity="warning">No grid metadata available</Alert>;
  }

  if (dataError) {
    return <Alert severity="error">Failed to load grid data: {JSON.stringify(dataError)}</Alert>;
  }

  const overlayLoading =
    (gridLogic.dataFetching && !dataLoading) || gridLogic.isBatchSaving || isDeleting;

  return (
    <Box sx={{ width: '100%' }}>
      <GridWithCommands
        metadata={gridMetadata}
        columns={parsedColumns}
        commands={gridMetadata?.commands}
        data={gridLogic.displayData}
        gridData={gridLogic.gridData}
        lookupMaps={gridLogic.lookupMapsAsMap}
        rawLookupItems={gridLogic.rawLookupItemsAsMap}
        dropdownBaseFilter={gridLogic.dropdownBaseFilter}
        totalCount={gridLogic.totalCount}
        page={gridLogic.gridFiltering.pageIndex}
        pageSize={gridLogic.gridFiltering.pageSize}
        onPageChange={gridLogic.gridFiltering.handlePageChange}
        onPageSizeChange={gridLogic.gridFiltering.handlePageSizeChange}
        onSortChange={gridLogic.gridFiltering.handleSortChange}
        onFilterChange={gridLogic.gridFiltering.handleFilterChange}
        onResetFilters={() => setIsResetFiltersDialogOpen(true)}
        loading={dataLoading}
        overlayLoading={overlayLoading}
        nodeKey={gridMetadata?.contentKey}
        inlineEditable={gridLogic.inlineEditable}
        onEdit={hasDetailForm ? handleEdit : undefined}
        onDelete={whenCommand(gridMetadata?.commands, 'delete', handleDelete)}
        onDataUpdate={gridLogic.handleDataUpdate}
        modifiedCells={gridLogic.modifiedCells}
        modifiedRows={gridLogic.modifiedRows}
        markedForDeletion={gridLogic.markedForDeletion}
        hasUnsavedChanges={gridLogic.hasUnsavedChanges}
        isBatchSaving={gridLogic.isBatchSaving}
        onRefresh={() => gridLogic.refetchGridData()}
        onSave={gridLogic.handleBatchSave}
        onCancel={gridLogic.handleCancelAll}
        onAddRow={gridLogic.handleAddNewRow}
        onAddRowWithData={gridLogic.handleAddNewRowWithData}
        navigate={handleNavigate}
        currentPath={pathname}
        showMetadataChips={false}
        searchTerm={gridLogic.gridFiltering.searchTerm}
        onSearchChange={gridLogic.gridFiltering.handleSearchChange}
        validationErrors={gridLogic.validationErrors}
      />
      <GridDialogs
        deleteDialogOpen={isDeleteDialogOpen}
        onCloseDeleteDialog={() => {
          setIsDeleteDialogOpen(false);
          setDeletingRow(null);
        }}
        onConfirmDelete={handleConfirmDelete}
        isDeleting={isDeleting}
        unsavedDialogOpen={navGuard.isUnsavedChangesDialogOpen}
        onCloseUnsaved={navGuard.handleCancelNavigation}
        onDiscardChanges={navGuard.handleDiscardAndNavigate}
        onSaveChanges={navGuard.handleSaveAndNavigate}
        savingChanges={gridLogic.isBatchSaving}
        saveError={navGuard.saveBeforeNavigateError}
        resetFiltersDialogOpen={isResetFiltersDialogOpen}
        onCloseResetFiltersDialog={() => setIsResetFiltersDialogOpen(false)}
        onConfirmResetFilters={handleConfirmResetFilters}
      />
    </Box>
  );
};
